import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { confirmDelete, handleDeleteError } from '../utils/deleteHelper';

const VEHICLE_TYPES = ['Car','Van','Bus','Motorcycle','Truck','Other'];
const FUEL_TYPES = ['Petrol','Diesel','CNG','Electric','Hybrid'];
const STATUS_COLORS = {
  available:   'bg-green-100 text-green-700',
  busy:        'bg-yellow-100 text-yellow-700',
  on_trip:     'bg-blue-100 text-blue-700',
  maintenance: 'bg-orange-100 text-orange-700',
  inactive:    'bg-gray-100 text-gray-500',
};
const STATUS_LABELS = {
  available: 'Available', busy: 'Busy (Planned)', on_trip: 'On Trip',
  maintenance: 'Maintenance', inactive: 'Inactive',
};
// Statuses locked by active trip — cannot be manually changed
const LOCKED_STATUSES = ['busy', 'on_trip'];
const EMPTY = {
  vehicle_number:'', vehicle_type:'Car', ownership:'company', brand:'', model:'',
  registration_number:'', fuel_type:'Petrol', capacity:4, department:'',
  current_km:0, status:'available', company_id:''
};

export default function Vehicles() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [vehicles, setVehicles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try { const res = await api.get('/api/vehicles'); setVehicles(res.data); }
    catch (err) { console.error('vehicles:', err); setVehicles([]); }
    if (isAdmin) {
      try { const r = await api.get('/api/companies'); setCompanies(r.data); }
      catch { setCompanies([]); }
    }
    setLoading(false);
  };

  useEffect(() => { if (user) load(); }, [user?.role]);

  const filtered = vehicles.filter(v => {
    const matchSearch = v.vehicle_number.toLowerCase().includes(search.toLowerCase()) ||
      (v.brand||'').toLowerCase().includes(search.toLowerCase()) ||
      (v.model||'').toLowerCase().includes(search.toLowerCase()) ||
      (v.department||'').toLowerCase().includes(search.toLowerCase());
    const matchStatus = !filterStatus || v.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, capacity: Number(form.capacity), current_km: Number(form.current_km) };
      if (editing) await api.put(`/api/vehicles/${editing}`, payload);
      else await api.post('/api/vehicles', payload);
      setShowForm(false); setForm(EMPTY); setEditing(null); load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
  };

  const handleEdit = (v) => {
    setForm({ vehicle_number:v.vehicle_number, vehicle_type:v.vehicle_type||'Car',
      ownership:v.ownership||'company', brand:v.brand||'', model:v.model||'',
      registration_number:v.registration_number||'', fuel_type:v.fuel_type||'Petrol',
      capacity:v.capacity||4, department:v.department||'', current_km:v.current_km||0,
      status:v.status||'available', company_id: v.company_id ? String(v.company_id) : '' });
    setEditing(v.id); setShowForm(true);
  };

  const handleDelete = async (id, num) => {
    if (!confirmDelete('vehicle', num)) return;
    try { await api.delete(`/api/vehicles/${id}`); load(); }
    catch (err) { handleDeleteError(err); }
  };

  const handleStatusChange = async (id, status) => {
    try { await api.put(`/api/vehicles/${id}/status`, { status }); load(); }
    catch (err) { alert(err.response?.data?.error || 'Status update failed'); }
  };

  const statusCounts = vehicles.reduce((acc, v) => { acc[v.status] = (acc[v.status]||0)+1; return acc; }, {});

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Vehicle Management</h1>
          <p className="text-gray-500 text-sm">{vehicles.length} vehicles registered</p>
        </div>
        <button onClick={() => { setForm(EMPTY); setEditing(null); setShowForm(true); }}
          className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm font-medium">
          ➕ Add Vehicle
        </button>
      </div>

      {/* Status filter buttons */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-wrap gap-3 items-center">
        <input type="search" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search vehicles..."
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]" />
        <div className="flex gap-2 flex-wrap">
          {[['','All',vehicles.length],['available','Available',statusCounts.available||0],['busy','Busy',statusCounts.busy||0],['on_trip','On Trip',statusCounts.on_trip||0],['maintenance','Maintenance',statusCounts.maintenance||0],['inactive','Inactive',statusCounts.inactive||0]].map(([val,label,count]) => (
            <button key={val} onClick={() => setFilterStatus(val)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filterStatus===val ? 'bg-blue-700 text-white' : 'border border-gray-200 hover:bg-gray-50 text-gray-600'}`}>
              {label} ({count})
            </button>
          ))}
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">{editing ? 'Edit Vehicle' : 'Add Vehicle'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Number *</label>
                <input type="text" value={form.vehicle_number} onChange={e => setForm({...form,vehicle_number:e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type</label>
                <select value={form.vehicle_type} onChange={e => setForm({...form,vehicle_type:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {VEHICLE_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                <input type="text" value={form.brand} onChange={e => setForm({...form,brand:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                <input type="text" value={form.model} onChange={e => setForm({...form,model:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label>
                <input type="text" value={form.registration_number} onChange={e => setForm({...form,registration_number:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fuel Type</label>
                <select value={form.fuel_type} onChange={e => setForm({...form,fuel_type:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {FUEL_TYPES.map(f => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ownership</label>
                <select value={form.ownership} onChange={e => setForm({...form,ownership:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="company">Company</option>
                  <option value="rental">Rental</option>
                  <option value="employee">Employee</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Capacity (seats)</label>
                <input type="number" value={form.capacity} onChange={e => setForm({...form,capacity:e.target.value})} min={1}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <input type="text" value={form.department} onChange={e => setForm({...form,department:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current KM</label>
                <input type="number" value={form.current_km} onChange={e => setForm({...form,current_km:e.target.value})} min={0}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select value={form.status} onChange={e => setForm({...form,status:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="available">Available</option>
                  <option value="busy">Busy (Planned Trip)</option>
                  <option value="on_trip">On Trip</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              {/* Company assignment */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Company *</label>
                {isAdmin ? (
                  <select value={form.company_id} onChange={e => setForm({...form,company_id:e.target.value})} required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">— Select Company —</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                ) : (
                  <input type="text" disabled value={user?.company_name || 'Your Company'}
                    className="w-full border border-gray-100 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-500" />
                )}
              </div>
              <div className="sm:col-span-2 flex gap-3 pt-2">
                <button type="submit" className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium">
                  {editing ? 'Update' : 'Add Vehicle'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}
                  className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-400">
          <div className="text-4xl animate-pulse mb-2">🚗</div>
          <p>Loading vehicles...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <div className="text-5xl mb-3">🚗</div>
          <p>No vehicles found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(v => (
            <div key={v.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-2xl">
                    {v.vehicle_type === 'Bus' ? '🚌' : v.vehicle_type === 'Truck' ? '🚛' : v.vehicle_type === 'Motorcycle' ? '🏍️' : v.vehicle_type === 'Van' ? '🚐' : '🚗'}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 font-mono">{v.vehicle_number}</h3>
                    <p className="text-sm text-gray-500">{v.brand} {v.model}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[v.status]||'bg-gray-100 text-gray-500'}`}>
                  {STATUS_LABELS[v.status] || v.status?.replace('_',' ')}
                </span>
              </div>
              <div className="space-y-1.5 text-sm text-gray-600">
                <div className="flex justify-between"><span className="text-gray-400">Type</span><span>{v.vehicle_type} · {v.fuel_type}</span></div>
                <div className="flex justify-between"><span className="text-gray-400">Reg No.</span><span className="font-mono">{v.registration_number||'—'}</span></div>
                <div className="flex justify-between"><span className="text-gray-400">Odometer</span><span>{(v.current_km||0).toLocaleString()} km</span></div>
                {v.department && <div className="flex justify-between"><span className="text-gray-400">Dept</span><span>{v.department}</span></div>}
                {isAdmin && <div className="flex justify-between"><span className="text-gray-400">Company</span><span className={v.company_name ? '' : 'text-red-400 font-semibold'}>{v.company_name || '⚠ No Company'}</span></div>}
              </div>
              <div className="mt-3 pt-3 border-t border-gray-50">
                <div className="flex gap-2 items-center">
                  {LOCKED_STATUSES.includes(v.status) ? (
                    /* Status locked — managed by trip lifecycle */
                    <div className="flex-1 flex items-center gap-1 border border-gray-100 bg-gray-50 rounded-lg px-2 py-1.5 text-xs text-gray-500">
                      <span>🔒</span>
                      <span>{STATUS_LABELS[v.status]} — complete/cancel trip to change</span>
                    </div>
                  ) : (
                    <select value={v.status} onChange={e => handleStatusChange(v.id, e.target.value)}
                      className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500">
                      <option value="available">Available</option>
                      <option value="maintenance">Maintenance</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  )}
                  <button onClick={() => handleEdit(v)} className="p-1.5 hover:bg-gray-100 rounded text-gray-400 hover:text-blue-600">✏️</button>
                  {isAdmin && <button onClick={() => handleDelete(v.id, v.vehicle_number)} className="p-1.5 hover:bg-gray-100 rounded text-gray-400 hover:text-red-500">🗑️</button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
